import { Box, Typography } from "@mui/material";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useAnalytics } from "@/hooks/useAnalytics";
import useGetArtist from "@/hooks/useGetArtist";
import { getDeviceInfo } from "@/lib/analyticsClient";
import { gatedPageKind } from "@/utils/artistGate";
import ArtistGateScreen from "./ArtistGateScreen";
import { useArtistGateLock } from "./useArtistGateLock";

const ARTIST_NOT_FOUND = "Artist not found";

export const GATE_REDIRECT_GRACE_MS = 12000;

const REDIRECT_PLATFORM_LABELS: Record<string, string> = {
  spotify: "Spotify",
  apple: "Apple Music",
  itunes: "Apple Music",
  youtube: "YouTube",
  soundcloud: "SoundCloud",
  tidal: "TIDAL",
  amazon: "Amazon Music",
};

const titleFromSlug = (slug: string) =>
  slug
    .replace(/[-_]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

function ArtistGateResolving({ label }: { label?: string }) {
  return (
    <Box
      role="status"
      aria-label={label || "Checking your access"}
      sx={{
        minHeight: "100dvh",
        backgroundColor: "#0b0b0d",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "18px",
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: "34px",
          height: "34px",
          borderRadius: "999px",
          border: "2px solid color-mix(in srgb, var(--logoColor, #d8c49a) 34%, transparent)",
          borderTopColor: "var(--logoColor, #d8c49a)",
          animation: "artist-gate-spin 760ms linear infinite",
          "@keyframes artist-gate-spin": { to: { transform: "rotate(360deg)" } },
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      />
      {label ? (
        <Typography sx={{ color: "var(--logoColor, #d8c49a)", fontFamily: "Poppins, sans-serif", fontSize: "15px", fontWeight: 500 }}>
          {label}
        </Typography>
      ) : null}
    </Box>
  );
}

export function ArtistGate({ children }: { children: ReactNode }) {
  const location = useLocation();
  const lock = useArtistGateLock();
  const { track } = useAnalytics();
  const artistData = useGetArtist({ artistId: lock.slug }, null, [lock.slug]);
  const unlockedRef = useRef<string | null>(null);
  const [redirectGaveUp, setRedirectGaveUp] = useState(false);

  useEffect(() => {
    setRedirectGaveUp(false);
    if (!lock.redirectPlatform) return;
    const timer = window.setTimeout(() => setRedirectGaveUp(true), GATE_REDIRECT_GRACE_MS);
    return () => window.clearTimeout(timer);
  }, [lock.redirectPlatform, location.pathname]);

  const openedBySubscription = Boolean(lock.slug) && lock.phase === "unlocked";

  useEffect(() => {
    if (!lock.slug || !openedBySubscription) return;
    if (unlockedRef.current === lock.slug) return;
    unlockedRef.current = lock.slug;
    if (getDeviceInfo().isBot) return;
    track("gate_unlocked", {
      eventCategory: "mailing",
      artist: lock.slug,
      metadata: { pagePath: location.pathname, pageKind: gatedPageKind(location.pathname) },
    });
  }, [location.pathname, lock.slug, openedBySubscription, track]);

  if (!lock.slug) return <>{children}</>;
  if (lock.redirectPlatform && !redirectGaveUp && artistData?.error !== ARTIST_NOT_FOUND) {
    const hidden = lock.resolving || lock.locked;
    return (
      <>
        {hidden ? <ArtistGateResolving label={`Opening ${REDIRECT_PLATFORM_LABELS[lock.redirectPlatform] || "the release"}`} /> : null}
        <Box hidden={hidden} sx={{ display: hidden ? "none" : "contents" }}>
          {children}
        </Box>
      </>
    );
  }
  if (lock.resolving) return <ArtistGateResolving />;
  if (!lock.locked) return <>{children}</>;
  if (artistData?.error === ARTIST_NOT_FOUND) return <>{children}</>;
  if (!artistData) return <ArtistGateResolving />;

  const loaded = artistData.artistData;

  return (
    <ArtistGateScreen
      slug={lock.slug}
      artist={{
        name: loaded?.name || titleFromSlug(lock.slug),
        image: loaded?.images?.[0]?.url,
        followers: loaded?.followers?.total,
        genres: loaded?.genres,
      }}
      phase={lock.phase}
      storedEmail={lock.storedEmail}
      onEmailChange={lock.setStoredEmail}
    />
  );
}

export default ArtistGate;
